import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

export default function ClinicLoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState("");

  // If already logged in, redirect to backend
  if (isAuthenticated) {
    navigate("/ai-artifacts", { replace: true });
    return null;
  }

  const canSubmit = email.trim() && password.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoggingIn(true);
    setError("");

    try {
      await login(email, password);
      navigate("/ai-artifacts", { replace: true });
    } catch {
      setError("Unable to sign in. Please try again.");
      setLoggingIn(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background-50">
      {/* Header */}
      <header className="h-16 lg:h-20 flex items-center px-4 md:px-6 lg:px-8 border-b border-background-100 bg-white flex-shrink-0">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-heart-pulse-line text-base"></i>
          </span>
          <span className="font-heading text-sm lg:text-base font-bold text-foreground-950 whitespace-nowrap">
            Quick Prosthetics
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-foreground-100 text-[10px] font-semibold text-foreground-700">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-lock-line text-[9px]"></i>
            </span>
            Secure Clinical Portal
          </span>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-start md:items-center justify-center px-4 overflow-y-auto">
        <div className="w-full max-w-md py-8 md:py-0">
          {/* Login Card */}
          <div className="bg-white rounded-2xl border border-background-200 p-6 md:p-8">
            {/* Icon */}
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="w-7 h-7 flex items-center justify-center text-primary-600">
                <i className="ri-hospital-line text-xl"></i>
              </span>
            </div>

            <h1 className="text-lg md:text-xl font-bold text-foreground-950 font-heading text-center mb-1">
              Clinic Partner Sign In
            </h1>
            <p className="text-xs text-foreground-500 text-center mb-6">
              Access your clinical backend, patient files, and ROI dashboard
            </p>

            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              data-readdy-form
            >
              <div>
                <label
                  className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold mb-1.5"
                  htmlFor="login-email"
                >
                  Email Address
                </label>
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="clinic@email.com"
                  autoComplete="email"
                />
              </div>

              <div>
                <label
                  className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold mb-1.5"
                  htmlFor="login-password"
                >
                  Password
                </label>
                <input
                  id="login-password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 animate-fadeSlideIn">
                  <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
                    <i className="ri-error-warning-line text-xs"></i>
                  </span>
                  <p className="text-[11px] text-red-700">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit || loggingIn}
                className={`w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                  canSubmit && !loggingIn
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer"
                    : "bg-foreground-100 text-foreground-400 cursor-not-allowed"
                }`}
              >
                {loggingIn ? (
                  <>
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-loader-4-line text-base animate-spin"></i>
                    </span>
                    Signing in...
                  </>
                ) : (
                  <>
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-login-box-line text-base"></i>
                    </span>
                    Sign In to Clinical Backend
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <span className="flex-1 h-px bg-background-200" />
              <span className="text-[10px] text-foreground-400 font-medium">
                OR
              </span>
              <span className="flex-1 h-px bg-background-200" />
            </div>

            {/* Link to signup */}
            <div className="text-center">
              <p className="text-[11px] text-foreground-500 mb-3">
                Don&apos;t have a clinic account yet?
              </p>
              <Link
                to="/clinic-signup"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-background-200 bg-background-50 text-sm font-semibold text-foreground-800 hover:border-primary-300 hover:bg-primary-50/50 transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-add-circle-line text-base"></i>
                </span>
                Create Clinic Account
              </Link>
            </div>
          </div>

          {/* Footer text */}
          <p className="text-[10px] text-foreground-400 text-center mt-4">
            Secure HIPAA-compliant clinical portal. All access is logged and
            audited.
          </p>
        </div>
      </main>
    </div>
  );
}
